'use client';

import type { Slot } from '@api/models';
import { useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';

import { BrowserApiClient } from '@website/lib/api/browser';

/**
 * Hook to fetch slots for an activity client-side, for MANAGEMENT/EDIT contexts.
 *
 * Requests `filterInvalidOnlinePayment: false` so the full paymentMethods (incl.
 * 'online' even when the pro isn't Stripe-onboarded) are returned — owners must be
 * able to see and edit online slots regardless of onboarding status. Buyer/booking
 * surfaces must NOT use this; they fetch with filtering on (the default) so an
 * un-onboarded pro's online option is hidden.
 *
 * @param activityId - The activity ID to fetch slots for
 * @param enabled - Whether to enable the query (default: true)
 * @returns Query result with slots array
 */
export function useActivitySlots(activityId: string | undefined, enabled: boolean = true) {
  const locale = useLocale();

  return useQuery<Slot[]>({
    queryKey: ['activitySlots', activityId, locale],
    queryFn: async () => {
      if (!activityId) return [];

      // Fetch activity via browser API client (sends auth cookies). Management
      // context: keep online slots visible regardless of Stripe onboarding.
      const activity = await BrowserApiClient.getActivityById(activityId, {
        filterInvalidOnlinePayment: false
      });

      // Return slots from the activity response
      return (activity?.slots as Slot[]) || [];
    },
    enabled: !!activityId && enabled,
    staleTime: 30 * 1000, // 30 seconds
    retry: 2
  });
}
